import { useEffect, useMemo, useRef } from 'react';
import {
  AnswerWithCategoryCounts,
  getCategoryCountsFromResponse,
  getCategoryOptionsFromCounts,
} from '../helpers/dashboardHelper';

interface Params {
  data: AnswerWithCategoryCounts | null | undefined;
  isFetching: boolean;
  dataSetKey: string;
  setSelectedCategories: (update: (prev: string[]) => string[]) => void;
}

/** Chips from the backend. Kept while the same agent and range reloads, dropped otherwise. */
export const useCategoryChips = ({
  data,
  isFetching,
  dataSetKey,
  setSelectedCategories,
}: Params) => {
  const lastChips = useRef<{ key: string; counts: Record<string, number> }>({
    key: '',
    counts: {},
  });
  const categoryCounts = useMemo(() => {
    const fresh = getCategoryCountsFromResponse(data);
    if (Object.keys(fresh).length > 0 || !isFetching) {
      lastChips.current = { key: dataSetKey, counts: fresh };
      return fresh;
    }
    return lastChips.current.key === dataSetKey ? lastChips.current.counts : {};
  }, [data, isFetching, dataSetKey]);
  const categoryOptions = useMemo(
    () => getCategoryOptionsFromCounts(categoryCounts),
    [categoryCounts]
  );

  // Drop picked chips that are gone from the data. Same array if nothing changed.
  useEffect(() => {
    if (!categoryOptions.length) {
      return;
    }
    setSelectedCategories(prev =>
      prev.every(category => categoryOptions.includes(category))
        ? prev
        : prev.filter(category => categoryOptions.includes(category))
    );
  }, [categoryOptions, setSelectedCategories]);

  return { categoryCounts, categoryOptions };
};
